import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { path } from 'ramda';
import { guid } from '../../common/ambient';
import classNames from 'classnames';
import AlertSet from './AlertSet';
import {
  useFetchAlertSets,
  useCreateAlertSet,
  useUpdateAlertSet,
  useRemoveAlertSet,
  useAlertSetSync
} from './redux/hooks';
import { useDoModal, useUpdateSetting, useUpdateGroupMembers } from '../user/redux/hooks';
import { useRefreshUser } from '../user/redux/hooks';
import { useGroupMemberInit } from './redux/useGroupMemberInit';
import { useGroupMemberSave } from './redux/useGroupMemberSave';
import { AlertSetMemberCard, alertSetOk } from '.';
import { isPlus, subIsTeams } from '../payment';
// import PropTypes from 'prop-types';

const MAX_MEMBERS = 5
const MAX_ALERT_SETS = 8
const ADMIN_DEFAULTS = { alertPrefs: [] }

export default function AlertSets() {
  const { fetchAlertSets, alertSets: _alertSets } = useFetchAlertSets();
  const { createAlertSet } = useCreateAlertSet();
  const { updateAlertSet } = useUpdateAlertSet();
  const { removeAlertSet } = useRemoveAlertSet();
  const { refreshUser } = useRefreshUser();
  const { doModal } = useDoModal();
  const { userChecked, info, updateSetting } = useUpdateSetting();
  const { updateGroupMembers: updateAlertSetGroupMembers } = useUpdateGroupMembers('alertSetGroupMembers');
  const [alertSets, setAlertSets] = useState([]);
  const [editingAlertSet, setEditingAlertSet] = useState(null);

  // Initialize group members from user info
  const { groupMembers, setGroupMembers } = useGroupMemberInit({
    userChecked,
    info,
    propertyName: 'alertSetGroupMembers',
    adminLabel: 'Group Admin (You)',
    adminDefaults: ADMIN_DEFAULTS
  });

  // Handle member save operations
  const { handleSaveMember } = useGroupMemberSave({
    groupMembers,
    updateGroupMembers: updateAlertSetGroupMembers,
    userActionTypePrefix: 'alert-set-group-member',
    showErrors: false
  });

  useAlertSetSync({
    alertSets,
    groupMembers,
    userId: path(['_id'], info)
  });
  // Refresh user data when component mounts to ensure we have the latest verification status
  useEffect(() => {
    if (userChecked && info._id) {
      refreshUser();
    }
  }, [refreshUser, userChecked, info._id]);

  useEffect(() => {
    try {
      if (userChecked && info._id) {
        fetchAlertSets({
          userId: info._id
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [fetchAlertSets, userChecked, info._id]);
  useEffect(() => {
    setAlertSets(_alertSets);
  }, [_alertSets, setAlertSets]);
  useEffect(() => {
    if (userChecked && (!info || !info._id)) {
      window.location.href = '/';
    }
  }, [userChecked, info]);

  const membersLeft = MAX_MEMBERS - groupMembers.length;

  if (!userChecked || !info || !info._id) {
    return <div>Loading...</div>;
  } else if (!isPlus(info) || (isPlus(info) && !subIsTeams(isPlus(info)))) {
    return (
      <div className="alert-alert-sets">
        <div className='block'>
          <h3>Why Choose AWN+ for Teams?</h3>
          <p>AWN+ for Teams keeps everyone connected through shared weather alerts and advanced data tools. It’s designed for groups that need to stay informed and coordinated without having to share accounts or dashboards.</p>
          <p>
            <ul>
              <li>
                <b>Comprehensive Team Data:</b> Unlock 15-day forecasts, 5-year data history, extended SMS alerts, and premium map layers for deeper, more actionable insights that support smarter planning and performance tracking
              </li>
              <li>
                <b>Seamless Collaboration:</b> Create custom team alerts for up to five members, including the admin, to keep everyone aligned in real time and ensure clear, consistent communication.
              </li>
              <li>
                <b>Focused Monitoring:</b> Use customized map filtering to view your stations, favorites, or NOAA sources for a clean, customized view tailored to your team’s operations.
              </li>
            </ul>
          </p>
          <p>
            <Link
              to='/subscriptions/subscribe?type=awn-plus'
              className='btn btn-primary'>
              Upgrade to AWN+ For Teams Today
            </Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="alert-alert-sets">
      <div className='block'>
        <h3>Team Alerts</h3>
        <p>This is the team you manage. Here, you can create and assign alert sets, add or remove members, and adjust their settings.</p>
      </div>
      <div className='block'>
        <h3>Manage team members</h3>
        <p>{membersLeft < MAX_MEMBERS ? `You can add up to ${membersLeft} more members.` : ''  }</p>
        {membersLeft > 0 && (
          <>
            <h4>Invite new team member</h4>
            <div className="invitation-buttons">
              <a
                className='btn btn-primary'
                onClick={() => {
                  doModal({
                    type: 'group-member-form',
                    data: {
                      member: null,
                      alertSets: alertSets,
                      showAlertSets: true,
                      smsEnabled: true,
                      userActionTypePrefix: 'alert-set-group-member',
                      onSave: handleSaveMember
                    }
                  })
                }}
              >
                Invite Member
              </a>
            </div>
          </>
        )}
        <h4>Members list</h4>
        <div className='group-members'>
          {groupMembers.map((member, i) => (
            <div className={classNames('group-member-wrap', `i-${i}`)} key={member._id}>
              <AlertSetMemberCard
                member={member}
                alertSets={alertSets}
                showUtilityMenu={true}
                onEdit={(member) => {
                  doModal({
                    type: 'group-member-form',
                    data: {
                      member,
                      alertSets: alertSets,
                      showAlertSets: true,
                      smsEnabled: true,
                      userActionTypePrefix: 'alert-set-group-member',
                      onSave: handleSaveMember
                    }
                  })
                }}
                onRemove={() => {
                  if (window.confirm('Are you sure you want to remove this member?')) {
                    updateAlertSetGroupMembers(groupMembers.filter((_, j) => i !== j));
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className='block'>
        <h3>Alert Sets</h3>
        <p>Create and customize up to {MAX_ALERT_SETS} alert sets for your team.</p>
        {alertSets.length < MAX_ALERT_SETS && (
          <button
            onClick={() => {
              doModal({
                type: 'create-alert-set',
                data: {
                  onCreate: newAlertSet => {
                    doModal(null);
                    const newAlertSets = [...alertSets, newAlertSet];
                    setAlertSets(newAlertSets);
                    setEditingAlertSet(newAlertSets.length - 1);
                  }
                }
              })
            }}
            className='btn btn-primary'
          >Create Alert Set</button>
        )}
        <div className='alert-sets'>
          {alertSets && alertSets.map((alertSet, i) => (
            <div className='alert-set-wrap' key={i}>
              <AlertSet 
                alertSet={alertSet} 
                editing={editingAlertSet === i} 
                onChange={async newAlertSet => {
                  if (newAlertSet._id) {
                    await updateAlertSet(newAlertSet._id, newAlertSet, false);
                  } else {
                    await createAlertSet(newAlertSet);
                    // Refresh alert sets to get the latest data with proper IDs
                    fetchAlertSets({
                      userId: info._id
                    });
                  }
                  setEditingAlertSet(null)
                }}
                onCancel={() => {
                  if (!alertSetOk(alertSet)) {
                    setAlertSets(alertSets.filter((_, j) => i !== j));
                  }
                  setEditingAlertSet(null)
                }}
              />
              <div className='buttons-wrap'>
                <div className='buttons-rounded'>
                  <a
                    onClick={() => setEditingAlertSet(i)}
                    className='trash glyphicon glyphicon-pencil'
                    title='Edit Alert Set'
                  />
                  <a
                    onClick={async () => {
                      // Check if we've hit the max limit
                      if (alertSets.length >= MAX_ALERT_SETS) {
                        alert(`You've reached the maximum of ${MAX_ALERT_SETS} alert sets. Please delete an existing alert set before duplicating.`);
                        return;
                      }

                      // Create a copy without the _id so it's treated as new
                      const duplicatedAlertSet = { ...alertSet };
                      delete duplicatedAlertSet._id;

                      // Add "(copy)" to the title
                      duplicatedAlertSet.title = `${alertSet.title} (copy)`;

                      // Save the duplicate to backend
                      await createAlertSet(duplicatedAlertSet);

                      // Refresh alert sets to get the latest data with proper IDs
                      fetchAlertSets({
                        userId: info._id
                      });
                    }}
                    className='trash glyphicon glyphicon-duplicate'
                    title='Duplicate Alert Set'
                  />
                  <a
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this alert set?')) {
                        setAlertSets(alertSets.filter((_, j) => i !== j));
                        await removeAlertSet(alertSet._id);
                        refreshUser();
                      }
                    }}
                    className='trash glyphicon glyphicon-trash'
                    title='Delete Alert Set'
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


AlertSets.propTypes = {};
AlertSets.defaultProps = {};

AlertSets.displayName = 'AlertSets'
